import React, { useMemo, memo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Flexbox, H5, P1, Caption } from "@talentbait/shared-components";
import CampaignWidgetLayout from "./CampaignWidgetLayout";

const StyledCampaignPreview = styled(CampaignWidgetLayout)`
  max-width: 500px;
  padding: 20px;

  .header-text {
    flex: 1;
  }
`;

const StyledPlaceholder = styled.div`
  overflow: hidden;

  &.text:empty {
    background-color: ${({ theme }) => theme.colors.neutral.gray100};
    width: 100%;
    height: 1rem;
    font-size: 1rem;
    border-radius: 1rem;

    &.short {
      max-width: 30%;
    }

    &.small {
      font-size: 0.75rem;
      height: 0.75rem;
      border-radius: 0.75rem;
    }
  }

  &.avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    flex-shrink: 0;
    background-color: ${({ theme }) => theme.colors.neutral.gray100};

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: 0% 50%;
    }
  }

  &.image {
    width: calc(100% + 40px);
    flex: 1;
    min-height: 0;
    max-height: 500px;
    margin: 0 -20px;

    &:empty {
      background-color: ${({ theme }) => theme.colors.neutral.gray100};
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

function parseOrigin(url) {
  try {
    const { hostname } = new URL(url);
    return hostname.replace("www.", "");
  } catch (err) {
    return url;
  }
}

function parseText(str = "") {
  const [text, tags] = str.split(/\r?\n(.*)/);
  return { text, tags };
}

const CampaignPreview = ({ campaign = {}, organization }) => {
  const { t } = useTranslation();

  const siteOrigin = useMemo(() => parseOrigin(campaign.jobPostingUrl), [campaign.jobPostingUrl]);
  const { text, tags } = useMemo(() => parseText(campaign.mainText), [campaign.mainText]);
  // the logo and name may be dynamic in the future, the component is prepared for it
  const logo = "/images/jobhappier.png";
  const name = "JobHappier";

  return (
    <StyledCampaignPreview as={Flexbox} gap={16}>
      <Flexbox row justifyContent="flex-start" alignItems="center" gap={16}>
        <StyledPlaceholder className="avatar">
          {!!logo && <img src={logo} alt={name} />}
        </StyledPlaceholder>
        <Flexbox gap={8} className="header-text">
          <StyledPlaceholder as={H5} className="text short">
            {name}
          </StyledPlaceholder>
          <StyledPlaceholder as={P1} className="text short small">
            {!!organization?.name && t("Sponsored")}
          </StyledPlaceholder>
        </Flexbox>
      </Flexbox>
      <Flexbox gap={8}>
        <StyledPlaceholder className="text">{text}</StyledPlaceholder>
        <StyledPlaceholder className="text">{tags}</StyledPlaceholder>
      </Flexbox>
      <StyledPlaceholder className="image">
        {campaign.image?.url && <img src={campaign.image.url} alt={campaign.image.name} />}
      </StyledPlaceholder>
      <Flexbox gap={8}>
        <StyledPlaceholder as={Caption} uppercase className="text short">
          {siteOrigin}
        </StyledPlaceholder>
        <StyledPlaceholder as={P1} bold className="text">
          {campaign.campaignName}
        </StyledPlaceholder>
      </Flexbox>
    </StyledCampaignPreview>
  );
};

export default memo(CampaignPreview);
